import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client';
import ProductCard from '../components/ProductCard';
import Toast, { useToast } from '../components/Toast';
import ConfirmDialog from '../components/ConfirmDialog';
import { useAuth } from '../contexts/AuthContext';
import './BidCentrePage.css';

function MultiSelect({ label, options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const toggle = (val) => {
    if (selected.includes(val)) onChange(selected.filter(v => v !== val));
    else onChange([...selected, val]);
  };
  const displayText = selected.length === 0 ? label : `${label} (${selected.length})`;

  return (
    <div className="multi-select" onMouseLeave={() => setOpen(false)}>
      <button className="input multi-select-btn" onClick={() => setOpen(!open)}>
        {displayText} <span className="multi-chevron">{open ? '\u25B2' : '\u25BC'}</span>
      </button>
      {open && (
        <div className="multi-dropdown">
          {options.map(opt => (
            <label key={opt} className="multi-option">
              <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)} />
              <span>{opt.charAt(0).toUpperCase() + opt.slice(1)}</span>
            </label>
          ))}
          {options.length === 0 && <div className="multi-empty">No options</div>}
        </div>
      )}
    </div>
  );
}

export default function WishlistPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();
  const [hideTarget, setHideTarget] = useState(null);
  const [filters, setFilters] = useState({ brand: [], color: [], grade: [], product_type: [], search: '' });

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['wishlist'],
    queryFn: () => api.get('/wishlist').then(r => r.data),
  });
  const { data: wishlistIds = [] } = useQuery({
    queryKey: ['wishlist-ids'],
    queryFn: () => api.get('/wishlist/ids').then(r => r.data),
  });

  const toggle = useMutation({
    mutationFn: (id) => {
      if (wishlistIds.includes(id)) return api.delete(`/wishlist/${id}`);
      return api.post('/wishlist', { product_id: id });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['wishlist'] }); qc.invalidateQueries({ queryKey: ['wishlist-ids'] }); },
  });

  const placeBid = useMutation({
    mutationFn: ({ productId, amount }) => api.post('/bids', { product_id: productId, amount_euro: amount }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wishlist'] });
      showToast('Your bid has been placed successfully.');
    },
  });

  const hideProduct = useMutation({
    mutationFn: (productId) => api.post(`/admin/products/${productId}/hide`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wishlist'] });
      setHideTarget(null);
    },
  });

  const saveNotes = useMutation({
    mutationFn: ({ productId, notes }) => api.patch(`/wishlist/${productId}/notes`, { notes }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['wishlist'] }); showToast('Note saved.'); },
  });

  const filterOptions = useMemo(() => {
    const brands = [...new Set(items.map(i => i.brand).filter(Boolean))].sort();
    const colors = [...new Set(items.map(i => i.color).filter(Boolean))].sort();
    const types = [...new Set(items.map(i => i.product_type).filter(Boolean))].sort();
    return { brands, colors, types };
  }, [items]);

  const filteredItems = useMemo(() => {
    return items.filter(p => {
      if (filters.brand.length && !filters.brand.includes(p.brand)) return false;
      if (filters.color.length && !filters.color.includes(p.color)) return false;
      if (filters.grade.length && !filters.grade.includes(p.grade_standardized)) return false;
      if (filters.product_type.length && !filters.product_type.includes(p.product_type)) return false;
      if (filters.search) {
        const term = filters.search.toLowerCase();
        const blob = `${p.clean_title || ''} ${p.brand || ''}`.toLowerCase();
        if (!blob.includes(term)) return false;
      }
      return true;
    });
  }, [items, filters]);

  const hasFilters = filters.brand.length || filters.color.length || filters.grade.length || filters.product_type.length || filters.search;

  if (isLoading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><div className="spinner" /></div>;

  return (
    <div className="bid-centre-page">
      <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 32, fontWeight: 400, marginBottom: 24 }}>Wishlist</h1>

      {items.length > 0 && (
        <div className="wishlist-controls">
          <input
            className="input search-input"
            placeholder="Search brand, model..."
            value={filters.search}
            onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
          />
          <div className="filters">
            <MultiSelect label="All brands" options={filterOptions.brands} selected={filters.brand} onChange={v => setFilters(f => ({ ...f, brand: v }))} />
            <MultiSelect label="All colors" options={filterOptions.colors} selected={filters.color} onChange={v => setFilters(f => ({ ...f, color: v }))} />
            <MultiSelect label="All grades" options={['S', 'A', 'AB', 'B', 'BC', 'C', 'D']} selected={filters.grade} onChange={v => setFilters(f => ({ ...f, grade: v }))} />
            <MultiSelect label="All categories" options={filterOptions.types} selected={filters.product_type} onChange={v => setFilters(f => ({ ...f, product_type: v }))} />
            {hasFilters && (
              <button className="btn btn-outline btn-sm" onClick={() => setFilters({ brand: [], color: [], grade: [], product_type: [], search: '' })}>
                Clear
              </button>
            )}
          </div>
        </div>
      )}

      {items.length === 0 && <p style={{ color: 'var(--color-text-muted)' }}>Your wishlist is empty.</p>}
      {items.length > 0 && filteredItems.length === 0 && (
        <p style={{ color: 'var(--color-text-muted)' }}>No items match your filters.</p>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 20 }}>
        {filteredItems.map(p => (
          <ProductCard
            key={p.id}
            product={p}
            isWishlisted={wishlistIds.includes(p.id)}
            onToggleWishlist={id => toggle.mutate(id)}
            onQuickBid={(productId, amount) => placeBid.mutate({ productId, amount })}
            isAdmin={user?.is_admin}
            onHide={id => setHideTarget(id)}
            showNotes={true}
            notesValue={p.notes || ''}
            onSaveNotes={(productId, notes) => saveNotes.mutate({ productId, notes })}
            userType={user?.user_type || 'individual'}
          />
        ))}
      </div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
      <ConfirmDialog
        open={!!hideTarget}
        title="Hide this item?"
        message="Sure you want to hide this item? It will no longer appear in the feed until you unhide it."
        confirmLabel="Hide"
        onConfirm={() => hideTarget && hideProduct.mutate(hideTarget)}
        onCancel={() => setHideTarget(null)}
      />
    </div>
  );
}
